import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  StarIcon,
  ScaleIcon,
  ClockIcon,
  ShieldCheckIcon,
  CheckIcon,
  ArrowRightIcon,
  PlayIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  MicrophoneIcon,
  BuildingOfficeIcon,
  UserGroupIcon
} from '../icons';
import { useAuth } from '../contexts/AuthContext';
import { HeroCourtAnimation } from '../components/HeroCourtAnimation';
import { CourtTranscriptionAnimation } from '../components/CourtTranscriptionAnimation';
import { ShowcaseAnimation } from '../components/ShowcaseAnimation';

const LandingPage = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [isGhana, setIsGhana] = useState(false);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDataAndDetectCountry = async () => {
      try {
        // Detect country
        const locationResponse = await fetch('https://ipapi.co/json/');
        let detectedIsGhana = false;
        try {
          const locationData = await locationResponse.json();
          if (locationData.country_code === 'GH') {
            detectedIsGhana = true;
          }
        } catch (locationError) {
          // console.log('Error detecting location:', locationError);
        }

        setIsGhana(detectedIsGhana);

        // Mock pricing plans for now
        const plans = [
          {
            id: 'starter',
            name: 'Starter Court',
            price: detectedIsGhana ? 450 : 75,
            price_local: detectedIsGhana ? 450 : null,
            price_usd: detectedIsGhana ? null : 75,
            description: 'Perfect for individual judges and small courts',
            features: [
              'Up to 50 hours/month transcription',
              'Basic AI summarization',
              'Court case management',
              'Email support',
              'Ghana legal context awareness'
            ],
            recommended: false,
            cta: 'Start Free Trial'
          },
          {
            id: 'professional',
            name: 'Professional Court',
            price: detectedIsGhana ? 900 : 150,
            price_local: detectedIsGhana ? 900 : null,
            price_usd: detectedIsGhana ? null : 150,
            description: 'Ideal for busy courtrooms and multiple judges',
            features: [
              'Up to 200 hours/month transcription',
              'Advanced AI features',
              'Legal citation assistance',
              'Priority support',
              'Custom legal templates',
              'Ghana legal precedent integration'
            ],
            recommended: true,
            cta: 'Most Popular'
          },
          {
            id: 'enterprise',
            name: 'Enterprise Court',
            price: detectedIsGhana ? 2250 : 375,
            price_local: detectedIsGhana ? 2250 : null,
            price_usd: detectedIsGhana ? null : 375,
            description: 'Complete solution for high-volume courts',
            features: [
              'Unlimited transcription hours',
              'Full AI suite',
              'Dedicated support team',
              'Custom integrations',
              'On-premise deployment option',
              'Advanced analytics & reporting',
              'Training for court staff'
            ],
            recommended: false,
            cta: 'Contact Sales'
          }
        ];
        setPricingPlans(plans);
      } catch (error) {
        // console.log('Error fetching data:', error);
        // Fallback pricing
        setPricingPlans([
          {
            id: 'starter',
            name: 'Starter Court',
            price: 75,
            description: 'Perfect for individual judges',
            features: ['50 hours/month transcription', 'Basic AI features'],
            recommended: false,
            cta: 'Start Free Trial'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchDataAndDetectCountry();
  }, []);


  const formatPrice = (plan) => {
    if (isGhana && plan.price_local) {
      return `₵${Number(plan.price_local).toLocaleString()}`;
    }
    return `$${Number(plan.price_usd || plan.price).toLocaleString()}`;
  };

  const features = [
    {
      icon: MicrophoneIcon,
      title: "Voice-to-Text Precision",
      description: "Advanced AI technology specifically trained on Ghanaian legal terminology and court proceedings. Achieve 99% accuracy in judicial transcription."
    },
    {
      icon: ClockIcon,
      title: "Real-time Transcription",
      description: "Live transcription during court proceedings with automatic speaker identification and legal context awareness."
    },
    {
      icon: ScaleIcon,
      title: "Legal Intelligence",
      description: "AI-powered legal research, citation suggestions, and precedent analysis specifically for Ghana's judicial system."
    },
    {
      icon: ShieldCheckIcon,
      title: "Secure & Confidential",
      description: "Bank-level security with end-to-end encryption, ensuring complete confidentiality of sensitive judicial proceedings."
    }
  ];

  const testimonials = [
    {
      name: "Hon. Justice Amoako",
      role: "High Court Judge",
      court: "Accra High Court",
      content: "Snwolley CourtScribe has revolutionized how we handle court proceedings. The accuracy in transcribing legal arguments and the Ghana legal context integration is exceptional.",
      rating: 5
    },
    {
      name: "Hon. Justice Osei",
      role: "Circuit Court Judge",
      court: "Kumasi Circuit Court",
      content: "The real-time transcription feature has improved our efficiency by 80%. We can now focus on delivering justice rather than paperwork.",
      rating: 5
    },
    {
      name: "Court Administrator",
      role: "Judicial Service",
      court: "Supreme Court of Ghana",
      content: "The platform's understanding of Ghana's legal system and procedures makes it invaluable for our courts. Implementation was seamless.",
      rating: 5
    }
  ];

  const stats = [
    { value: '99.5%', label: 'Transcription Accuracy' },
    { value: '50+', label: 'Ghanaian Courts Using' },
    { value: '10,000+', label: 'Hours Transcribed' },
    { value: '24/7', label: 'AI Support' }
  ];

  const legalFeatures = [
    {
      icon: ScaleIcon,
      title: "Case Management",
      description: "Organize and manage court cases with intelligent categorization and search capabilities."
    },
    {
      icon: DocumentTextIcon,
      title: "Legal Summaries",
      description: "Automated generation of case summaries, key points, and legal arguments."
    },
    {
      icon: AcademicCapIcon,
      title: "Legal Research",
      description: "AI-powered research assistant for Ghanaian laws, statutes, and case precedents."
    },
    {
      icon: BuildingOfficeIcon,
      title: "Court Integration",
      description: "Seamless integration with existing court management systems and workflows."
    },
    {
      icon: UserGroupIcon,
      title: "Multi-speaker Detection",
      description: "Automatic identification and separation of judges, lawyers, witnesses, and other participants."
    },
    {
      icon: ShieldCheckIcon,
      title: "Compliance & Security",
      description: "Full compliance with Ghana Judicial Service standards and data protection regulations."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-legal-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="/logo.png" 
                alt="Snwolley CourtScribe Logo" 
                className="h-8 w-8 rounded-lg"
              />
              <span className="ml-2 text-xl font-bold text-legal-900">Snwolley CourtScribe</span>
            </div>

            {/* Desktop Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-legal-600 hover:text-legal-900 transition-colors">Features</a>
              <a href="#legal-features" className="text-legal-600 hover:text-legal-900 transition-colors">Legal Tools</a>
              <a href="#pricing" className="text-legal-600 hover:text-legal-900 transition-colors">Pricing</a>
              <a href="#testimonials" className="text-legal-600 hover:text-legal-900 transition-colors">Testimonials</a>
              {!authLoading && !isAuthenticated && (
                <Link
                  to="/login"
                  className="gradient-court text-white px-4 py-2 rounded-lg hover:from-court-600 hover:to-amber-700 transition-all duration-200 transform hover:scale-105"
                >
                  Sign in
                </Link>
              )}
              {!authLoading && isAuthenticated && (
                <Link
                  to="/dashboard"
                  className="gradient-court text-white px-4 py-2 rounded-lg hover:from-court-600 hover:to-amber-700 transition-all duration-200 transform hover:scale-105"
                >
                  Go to Dashboard
                </Link>
              )}
            </div>

            {/* Mobile Navigation */}
            <div className="md:hidden flex items-center">
              {/* Mobile Sign In Button */}
              {!authLoading && !isAuthenticated && (
                <Link
                  to="/login"
                  className="gradient-court text-white px-3 py-2 rounded-lg text-sm font-medium hover:from-court-600 hover:to-amber-700 transition-all duration-200"
                >
                  Sign in
                </Link>
              )}
              {!authLoading && isAuthenticated && (
                <Link
                  to="/dashboard"
                  className="gradient-court text-white px-3 py-2 rounded-lg text-sm font-medium hover:from-court-600 hover:to-amber-700 transition-all duration-200"
                >
                  Dashboard
                </Link>
              )}
            </div>
          </div>

        </div>
      </nav>

      {/* Hero Section */}
      <section className="gradient-legal py-16 lg:py-24 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 35px,
              rgba(139, 69, 19, 0.1) 35px,
              rgba(139, 69, 19, 0.1) 70px
            )`
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8 items-center">
            <div className="lg:col-span-6">
              <div className="text-center lg:text-left">
                {/* Ghana Badge */}
                <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-court-100 text-court-800 mb-6 animate-fade-in">
                  <div className="w-2 h-2 bg-court-600 rounded-full mr-2 animate-pulse"></div>
                  🇬🇭 Built Exclusively for Ghana's Courts
                </div>

                {/* Main Headline */}
                <h1 className="text-4xl sm:text-5xl lg:text-5xl font-bold text-legal-900 leading-tight mb-6">
                  Stop Typing.
                  <br />
                  Start Delivering
                  <span className="text-court">
                    &nbsp;Justice Faster
                  </span>
                </h1>

                {/* Sub-headline */}
                <p className="mt-6 text-xl text-legal-600 max-w-3xl leading-relaxed">
                  From 8-hour transcription marathons to instant courtroom records.
                  <strong> Ghana's first AI</strong> that understands your legal terminology,
                  cites precedents correctly, and gives you back 75% of your administrative time.
                </p>

                {/* Social Proof Banner */}
                <div className="mt-8 p-4 bg-legal-50 rounded-xl border border-legal-200">
                  <div className="flex items-center justify-center lg:justify-start space-x-6 text-sm">
                    <div className="flex items-center space-x-2">
                      <div className="flex -space-x-2">
                        <div className="w-6 h-6 bg-court-600 rounded-full border-2 border-white"></div>
                        <div className="w-6 h-6 bg-accent-600 rounded-full border-2 border-white"></div>
                        <div className="w-6 h-6 bg-amber-600 rounded-full border-2 border-white"></div>
                      </div>
                      <span className="text-legal-700 font-medium">50+ Ghanaian Courts</span>
                    </div>
                    <div className="text-legal-400">•</div>
                    <div className="text-legal-700">
                      <span className="font-bold text-court-600">99.5%</span> Accuracy
                    </div>
                    <div className="text-legal-400">•</div>
                    <div className="text-legal-700">
                      <span className="font-bold text-accent-600">10,000+</span> Hours Saved
                    </div>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <Link
                    to={!authLoading && isAuthenticated ? "/dashboard" : "/login"}
                    className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white gradient-court rounded-xl hover:from-court-600 hover:to-amber-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    {!authLoading && isAuthenticated ? "Go to Dashboard" : "Start Free Trial"}
                    <ArrowRightIcon className="ml-2 h-5 w-5" />
                  </Link>
                  <button className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-legal-700 bg-white border-2 border-legal-300 rounded-xl hover:border-legal-400 transition-all duration-200">
                    <PlayIcon className="mr-2 h-5 w-5" />
                    Watch Demo
                  </button>
                </div>

                {/* Trust Indicators */}
                <div className="mt-12">
                  <p className="text-sm text-legal-500 mb-4">Trusted by Ghana's Judicial Service</p>
                  <div className="flex items-center justify-center lg:justify-start space-x-6">
                    <div className="flex items-center space-x-2 bg-white rounded-lg px-3 py-2 shadow-sm">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-medium text-legal-700">Live Transcription</span>
                    </div>
                    <div className="flex items-center space-x-2 bg-white rounded-lg px-3 py-2 shadow-sm">
                      <span className="text-xs font-bold text-court-600">99.5%</span>
                      <span className="text-xs font-medium text-legal-700">Accuracy</span>
                    </div>
                    <div className="flex items-center space-x-2 bg-white rounded-lg px-3 py-2 shadow-sm">
                      <span className="text-xs font-bold text-accent-600">24/7</span>
                      <span className="text-xs font-medium text-legal-700">Support</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Animation */}
            <div className="mt-16 lg:mt-0 lg:col-span-6">
              <div className="relative px-8 py-8">
                <HeroCourtAnimation />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Before & After Transformation Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800 mb-4">
              ⚡ The Transformation
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-legal-900 mb-4">
              Your Courtroom, Transformed
            </h2>
            <p className="text-xl text-legal-600 max-w-3xl mx-auto">
              See how Ghanaian courts are eliminating paperwork bottlenecks and delivering justice 3x faster
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Before */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl transform rotate-1"></div>
              <div className="relative bg-white rounded-2xl p-8 shadow-xl border-2 border-red-100">
                <div className="flex items-center mb-6">
                  <div className="bg-red-100 text-red-600 p-3 rounded-full">
                    <ClockIcon className="h-6 w-6" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-bold text-red-600">Before CourtScribe</h3>
                    <p className="text-sm text-red-500">The Traditional Way</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="text-red-500 mr-3 mt-1">✗</div>
                    <div>
                      <div className="font-semibold text-legal-900">8+ Hours of Manual Typing</div>
                      <div className="text-legal-600 text-sm">Staff spend entire days typing transcripts</div>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="text-red-500 mr-3 mt-1">✗</div>
                    <div>
                      <div className="font-semibold text-legal-900">Case Backlogs for Months</div>
                      <div className="text-legal-600 text-sm">Delayed justice due to paperwork bottlenecks</div>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="text-red-500 mr-3 mt-1">✗</div>
                    <div>
                      <div className="font-semibold text-legal-900">Inconsistent Legal Citations</div>
                      <div className="text-legal-600 text-sm">Human errors in precedent references</div>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="text-red-500 mr-3 mt-1">✗</div>
                    <div>
                      <div className="font-semibold text-legal-900">Missing Witness Testimonies</div>
                      <div className="text-legal-600 text-sm">Critical details lost in manual notes</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* After */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl transform -rotate-1"></div>
              <div className="relative bg-white rounded-2xl p-8 shadow-xl border-2 border-green-100">
                <div className="flex items-center mb-6">
                  <div className="bg-green-100 text-green-600 p-3 rounded-full">
                    <CheckIcon className="h-6 w-6" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-bold text-green-600">After CourtScribe</h3>
                    <p className="text-sm text-green-500">The AI-Powered Way</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="text-green-500 mr-3 mt-1">✓</div>
                    <div>
                      <div className="font-semibold text-legal-900">Instant Transcription</div>
                      <div className="text-legal-600 text-sm">Real-time text as proceedings happen</div>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="text-green-500 mr-3 mt-1">✓</div>
                    <div>
                      <div className="font-semibold text-legal-900">Same-Day Case Closure</div>
                      <div className="text-legal-600 text-sm">Eliminate backlogs, deliver justice faster</div>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="text-green-500 mr-3 mt-1">✓</div>
                    <div>
                      <div className="font-semibold text-legal-900">Perfect Legal Citations</div>
                      <div className="text-legal-600 text-sm">AI suggests Ghana precedents accurately</div>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="text-green-500 mr-3 mt-1">✓</div>
                    <div>
                      <div className="font-semibold text-legal-900">Complete, Searchable Records</div>
                      <div className="text-legal-600 text-sm">Every word captured, easily searchable</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Transformation Stats */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-court-500 to-amber-600 text-white rounded-full shadow-lg">
              <span className="font-bold text-lg">75% Time Saved</span>
              <span className="mx-3">•</span>
              <span>100% Accuracy</span>
              <span className="mx-3">•</span>
              <span>Instant Access</span>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Court Showcase */}
      <section className="py-16 gradient-legal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800 mb-4">
              🎯 Live Demonstration
            </div>
            <h2 className="text-3xl font-bold text-legal-900 mb-4">
              Watch Justice Happen in Real-Time
            </h2>
            <p className="text-lg text-legal-600 max-w-3xl mx-auto">
              See exactly how 50+ Ghanaian courts are transforming their proceedings.
              <br />
              <strong>No more typing. No more waiting. Just instant, accurate justice.</strong>
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Main Animation - Spans 2 columns */}
            <div className="lg:col-span-2 md:col-span-2 h-96">
              <CourtTranscriptionAnimation />
            </div>

            {/* Side Stats */}
            <div className="space-y-6">
              {/* Active Courts */}
              <div className="bg-white rounded-2xl border border-legal-200 p-6 h-44 flex flex-col justify-center">
                <div className="text-center">
                  <div className="text-3xl font-bold text-court-600 mb-2">50+</div>
                  <div className="text-legal-700 font-medium">Active Courts</div>
                  <div className="text-sm text-legal-500 mt-2">
                    Supreme to District courts
                  </div>
                </div>
              </div>

              {/* Hours Transcribed */}
              <div className="bg-gradient-to-br from-accent-50 to-court-50 rounded-2xl border border-legal-200 p-6 h-44 flex flex-col justify-center">
                <div className="text-center">
                  <div className="text-3xl font-bold text-accent-600 mb-2">10,000+</div>
                  <div className="text-legal-700 font-medium">Hours Transcribed</div>
                  <div className="text-sm text-legal-500 mt-2">
                    99.5% accuracy maintained
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Court Types Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            <div className="bg-white rounded-xl border border-legal-200 p-4 text-center hover:shadow-lg transition-shadow">
              <div className="text-2xl mb-2">⚖️</div>
              <div className="text-sm font-medium text-legal-700">Supreme Court</div>
              <div className="text-xs text-legal-500 mt-1">Constitutional matters</div>
            </div>
            <div className="bg-white rounded-xl border border-legal-200 p-4 text-center hover:shadow-lg transition-shadow">
              <div className="text-2xl mb-2">🏛️</div>
              <div className="text-sm font-medium text-legal-700">High Court</div>
              <div className="text-xs text-legal-500 mt-1">Civil & Criminal cases</div>
            </div>
            <div className="bg-white rounded-xl border border-legal-200 p-4 text-center hover:shadow-lg transition-shadow">
              <div className="text-2xl mb-2">⚖️</div>
              <div className="text-sm font-medium text-legal-700">Circuit Court</div>
              <div className="text-xs text-legal-500 mt-1">Regional proceedings</div>
            </div>
            <div className="bg-white rounded-xl border border-legal-200 p-4 text-center hover:shadow-lg transition-shadow">
              <div className="text-2xl mb-2">🏛️</div>
              <div className="text-sm font-medium text-legal-700">District Court</div>
              <div className="text-xs text-legal-500 mt-1">Local cases</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - 3 Simple Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-court-100 text-court-800 mb-4">
              🚀 Get Started in Minutes
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-legal-900 mb-4">
              From Courtroom to AI, Instantly
            </h2>
            <p className="text-xl text-legal-600 max-w-3xl mx-auto">
              No technical training required. Start transcribing your first case in under 5 minutes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative">
              <div className="text-center">
                <div className="relative inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-court-500 to-amber-600 rounded-full text-white text-2xl font-bold mb-6">
                  1
                </div>
                <div className="absolute top-8 left-1/2 ml-8 w-full h-0.5 bg-gradient-to-r from-court-500 to-court-400 hidden md:block" style={{ width: 'calc(100% + 2rem)' }}></div>

                <h3 className="text-xl font-bold text-legal-900 mb-4">Connect Your Microphone</h3>
                <p className="text-legal-600 mb-6">
                  Place a microphone in your courtroom. Our AI automatically detects judges, lawyers, and witnesses.
                </p>
                <div className="bg-legal-50 rounded-lg p-4">
                  <div className="flex items-center justify-center space-x-2 text-sm text-legal-600">
                    <MicrophoneIcon className="h-4 w-4 text-court-600" />
                    <span>No special equipment needed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="text-center">
                <div className="relative inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-court-500 to-amber-600 rounded-full text-white text-2xl font-bold mb-6">
                  2
                </div>
                <div className="absolute top-8 left-1/2 ml-8 w-full h-0.5 bg-gradient-to-r from-court-500 to-court-400 hidden md:block" style={{ width: 'calc(100% + 2rem)' }}></div>

                <h3 className="text-xl font-bold text-legal-900 mb-4">Start Your Proceedings</h3>
                <p className="text-legal-600 mb-6">
                  Begin your court session. Watch as every word is transcribed in real-time with 99.5% accuracy.
                </p>
                <div className="bg-legal-50 rounded-lg p-4">
                  <div className="flex items-center justify-center space-x-2 text-sm text-legal-600">
                    <ClockIcon className="h-4 w-4 text-court-600" />
                    <span>Live transcription as you speak</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="text-center">
                <div className="relative inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-court-500 to-amber-600 rounded-full text-white text-2xl font-bold mb-6">
                  3
                </div>

                <h3 className="text-xl font-bold text-legal-900 mb-4">Get Perfect Records</h3>
                <p className="text-legal-600 mb-6">
                  Receive complete, searchable transcripts with legal citations, summaries, and precedent suggestions instantly.
                </p>
                <div className="bg-legal-50 rounded-lg p-4">
                  <div className="flex items-center justify-center space-x-2 text-sm text-legal-600">
                    <DocumentTextIcon className="h-4 w-4 text-court-600" />
                    <span>Export in any format</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Badge */}
          <div className="mt-16 text-center">
            <div className="inline-flex items-center px-6 py-3 bg-green-50 border border-green-200 rounded-full">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-3 animate-pulse"></div>
              <span className="text-green-700 font-medium">
                <strong>No installation required</strong> • Works on any device • Ghana Judicial Service approved
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-court-600">{stat.value}</div>
                <div className="mt-2 text-sm text-legal-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 gradient-legal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-court-100 text-court-800 mb-4">
              🤖 AI Superpowers
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-legal-900 mb-4">
              Why Ghana's Top Judges Choose CourtScribe
            </h2>
            <p className="text-xl text-legal-600 max-w-3xl mx-auto">
              Finally, AI that speaks your language. Built specifically for Ghana's courts,
              legal terminology, and justice system.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {features.map((feature, index) => (
              <div key={index} className="flex">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 gradient-court rounded-xl">
                    <feature.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-6">
                  <h3 className="text-xl font-semibold text-legal-900 mb-3">{feature.title}</h3>
                  <p className="text-legal-600 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Features Section */}
      <section id="legal-features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800 mb-4">
              ⚖️ Legal Intelligence
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-legal-900 mb-4">
              Your Complete Courtroom Command Center
            </h2>
            <p className="text-xl text-legal-600 max-w-3xl mx-auto">
              Every tool you need to run your courtroom efficiently.
              From case management to precedent research - all in one powerful platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {legalFeatures.map((feature, index) => (
              <div key={index} className="card-legal hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center justify-center h-12 w-12 gradient-court rounded-xl mb-4">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-legal-900 mb-3">{feature.title}</h3>
                <p className="text-legal-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* <section id="testimonials" className="py-20 gradient-legal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 mb-4">
              🗣️ From The Bench
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-legal-900 mb-4">
              Ghana's Judges Speak Out
            </h2>
            <p className="text-xl text-legal-600">
              Real stories from judicial officers who transformed their courtrooms with CourtScribe
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-legal-200">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-amber-400 fill-current" />
                  ))}
                </div>
                <p className="text-legal-700 mb-6 leading-relaxed">"{testimonial.content}"</p>
                <div className="border-t border-legal-200 pt-4">
                  <h4 className="font-semibold text-legal-900">{testimonial.name}</h4>
                  <p className="text-sm text-legal-600">{testimonial.role}</p>
                  <p className="text-sm text-court-600">{testimonial.court}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Pricing Section */}
      {/* <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-court-100 text-court-800 mb-4">
              💰 Investment in Justice
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-legal-900 mb-4">
              Pricing That Serves Ghana's Courts
            </h2>
            <p className="text-xl text-legal-600">
              Special pricing for Ghanaian judicial institutions. 
              <br />
              <strong>ROI in weeks, not months.</strong>
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {loading ? (
              [1, 2, 3].map((index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg animate-pulse">
                  <div className="text-center mb-8">
                    <div className="h-8 bg-legal-200 rounded mb-2"></div>
                    <div className="h-16 bg-legal-200 rounded mb-4"></div>
                    <div className="h-6 bg-legal-200 rounded"></div>
                  </div>
                  <div className="space-y-3 mb-8">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="h-4 bg-legal-200 rounded"></div>
                    ))}
                  </div>
                  <div className="h-12 bg-legal-200 rounded"></div>
                </div>
              ))
            ) : (
              pricingPlans.map((plan) => (
              <div 
                key={plan.id} 
                className={`bg-white rounded-2xl p-8 shadow-lg relative ${
                  plan.recommended ? 'ring-2 ring-court-500 transform scale-105' : ''
                }`}
              >
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="gradient-court text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-legal-900 mb-2">{plan.name}</h3>
                  <p className="text-legal-600 mb-4">{plan.description}</p>
                  <div className="flex items-center justify-center">
                    <span className="text-4xl font-bold text-court-600">{formatPrice(plan)}</span>
                    <span className="text-legal-600 ml-2">/month</span>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <CheckIcon className="h-5 w-5 text-court-500 mr-3 flex-shrink-0" />
                      <span className="text-legal-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {plan.cta === 'Contact Sales' ? (
                  <a
                    href="mailto:sales@snwolley.ai?subject=Enterprise Plan Inquiry&body=Hi, I'm interested in learning more about your Enterprise plan for our court."
                    className={`w-full py-3 px-6 rounded-lg font-medium text-center block transition-all duration-200 ${
                      plan.recommended
                        ? 'gradient-court text-white hover:from-court-600 hover:to-amber-700 transform hover:scale-105'
                        : 'bg-legal-100 text-legal-900 hover:bg-legal-200'
                    }`}
                  >
                    {plan.cta}
                  </a>
                ) : (
                  <Link
                    to="/login"
                    className={`w-full py-3 px-6 rounded-lg font-medium text-center block transition-all duration-200 ${
                      plan.recommended
                        ? 'gradient-court text-white hover:from-court-600 hover:to-amber-700 transform hover:scale-105'
                        : 'bg-legal-100 text-legal-900 hover:bg-legal-200'
                    }`}
                  >
                    {plan.cta}
                  </Link>
                )}
              </div>
            ))
            )}
          </div>

          <div className="text-center mt-12">
            <p className="text-legal-600">
              Need a custom solution for your court?{' '}
              <a 
                href="mailto:sales@snwolley.ai?subject=Custom Court Solution&body=Hi, I'm interested in discussing a custom solution for our court." 
                className="text-court-600 hover:text-court-700 font-medium"
              >
                Contact our judicial solutions team
              </a>
            </p>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-20 gradient-court">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white font-medium mb-8">
            <div className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></div>
            Limited Time: Free Implementation Setup (₵5,000 value)
          </div>

          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            The Future of Ghana's Justice System
            <br />
            <span className="text-court-200">Starts in Your Courtroom</span>
          </h2>
          <p className="text-xl text-court-100 mb-12 max-w-3xl mx-auto">
            Don't let another day go by with manual transcription bottlenecks.
            Join 50+ Ghanaian courts delivering justice 3x faster with AI-powered precision.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={!authLoading && isAuthenticated ? "/dashboard" : "/login"}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-court-600 bg-white rounded-xl hover:bg-legal-50 transform hover:scale-105 transition-all duration-200 shadow-lg"
            >
              {!authLoading && isAuthenticated ? "Go to Dashboard" : "Start Free Trial"}
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
            <a
              href="mailto:marketing@npontu.com?subject=Live Demo Request&body=Hi, I would like to schedule a live demo of Snwolley CourtScribe for our court."
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white border-2 border-white rounded-xl hover:bg-white hover:text-court-600 transition-all duration-200"
            >
              Schedule Demo
            </a>
          </div>
          <p className="mt-6 text-sm text-court-100">
            No credit card required • 14-day free trial • Ghana judicial service approved
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-legal-900 text-legal-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <img 
                  src="/logo.png" 
                  alt="Snwolley CourtScribe Logo" 
                  className="h-8 w-8 rounded-lg"
                />
                <span className="ml-2 text-xl font-bold text-white">Snwolley CourtScribe</span>
              </div>
              <p className="text-legal-400 mb-6 max-w-md">
                AI-powered court transcription platform designed for Ghana's judicial system.
                Transcribe, summarize, and analyze court proceedings with advanced artificial intelligence.
              </p>
              <div className="flex space-x-4">
                <a href="https://snwolley.ai" target="_blank" rel="noopener noreferrer" className="text-legal-400 hover:text-white transition-colors">
                  <GlobeAltIcon className="h-6 w-6" />
                </a>
                <a href="mailto:marketing@npontu.com?subject=Contact Us&body=Hi, I would like to get in touch with your team." className="text-legal-400 hover:text-white transition-colors">
                  <EnvelopeIcon className="h-6 w-6" />
                </a>
                <span className="text-legal-400">
                  <PhoneIcon className="h-6 w-6" />
                </span>
              </div>
            </div>

            {/* Product Links */}
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#legal-features" className="hover:text-white transition-colors">Legal Tools</a></li>
                <li><span className="text-legal-300">Pricing</span></li>
                <li><span className="text-legal-300">API</span></li>
              </ul>
            </div>

            {/* Support Links */}
            <div>
              <h3 className="text-white font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><a href="mailto:marketing@npontu.com?subject=Contact Us&body=Hi, I would like to get in touch with your team." className="hover:text-white transition-colors">Contact Us</a></li>
                <li><span className="text-legal-300">Privacy Policy</span></li>
                <li><span className="text-legal-300">Terms of Service</span></li>
                <li><span className="text-legal-300">Security</span></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-legal-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-legal-400 text-sm">
              © 2025 Snwolley CourtScribe. All rights reserved.
            </p>
            <p className="text-legal-400 text-sm mt-4 md:mt-0">
              Proudly serving Ghana's Judicial Service
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;